import { getBackupCounts, getSnapshotIdsToDelete } from './backup';
import type { BackupPayload, Category, ContentStore, Note, PlannerData, PlannerEvent, PlannerRecord, Settings, ShoppingItem, Snapshot, Task, TaskOccurrence } from '../types';

export const DB_NAME = 'spooky-plans-db';
export const DB_VERSION = 2;

type StoreRecordMap = {
  tasks: Task;
  taskOccurrences: TaskOccurrence;
  events: PlannerEvent;
  notes: Note;
  shoppingItems: ShoppingItem;
  categories: Category;
  snapshots: Snapshot;
};

type StoreName = keyof StoreRecordMap;
const PLANNER_STORE_NAMES = ['tasks', 'taskOccurrences', 'events', 'notes', 'shoppingItems', 'categories'] as const;
export const SETTINGS_KEY = 'spookyPlans.settings.v4';

const DEFAULT_CATEGORIES: readonly Category[] = [
  { id: 'category-personal', name: 'Personal', normalizedName: 'personal', color: '#da8825', icon: 'sparkles', kind: 'default' },
  { id: 'category-work', name: 'Work', normalizedName: 'work', color: '#76567b', icon: 'briefcase', kind: 'default' },
  { id: 'category-wellness', name: 'Wellness', normalizedName: 'wellness', color: '#5f8f73', icon: 'heart', kind: 'default' },
  { id: 'category-errands', name: 'Errands', normalizedName: 'errands', color: '#b14622', icon: 'shopping-bag', kind: 'default' }
];

export class PlannerRollbackError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PlannerRollbackError';
  }
}

let databasePromise: Promise<IDBDatabase> | null = null;
const changeChannel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('spooky-plans-db-v2');

const createIndex = (store: IDBObjectStore, name: string, keyPath: string | string[], options?: IDBIndexParameters) => {
  if (!store.indexNames.contains(name)) store.createIndex(name, keyPath, options);
};

const prepareStore = (database: IDBDatabase, transaction: IDBTransaction, name: StoreName) =>
  database.objectStoreNames.contains(name)
    ? transaction.objectStore(name)
    : database.createObjectStore(name, { keyPath: 'id' });

const configureSchema = (database: IDBDatabase, transaction: IDBTransaction) => {
  const tasks = prepareStore(database, transaction, 'tasks');
  createIndex(tasks, 'dueDate', 'dueDate');
  createIndex(tasks, 'categoryId', 'categoryId');
  createIndex(tasks, 'status', 'status');
  createIndex(tasks, 'favorite', 'favorite');
  createIndex(tasks, 'updatedAt', 'updatedAt');

  const occurrences = prepareStore(database, transaction, 'taskOccurrences');
  createIndex(occurrences, 'taskDate', ['taskId', 'occurrenceDate'], { unique: true });
  createIndex(occurrences, 'taskId', 'taskId');

  const events = prepareStore(database, transaction, 'events');
  createIndex(events, 'date', 'date');
  createIndex(events, 'categoryId', 'categoryId');
  createIndex(events, 'favorite', 'favorite');
  createIndex(events, 'updatedAt', 'updatedAt');

  const notes = prepareStore(database, transaction, 'notes');
  createIndex(notes, 'categoryId', 'categoryId');
  createIndex(notes, 'favorite', 'favorite');
  createIndex(notes, 'pinned', 'pinned');
  createIndex(notes, 'updatedAt', 'updatedAt');

  const shopping = prepareStore(database, transaction, 'shoppingItems');
  createIndex(shopping, 'categoryId', 'categoryId');
  createIndex(shopping, 'completed', 'completed');
  createIndex(shopping, 'favorite', 'favorite');
  createIndex(shopping, 'updatedAt', 'updatedAt');

  const categories = prepareStore(database, transaction, 'categories');
  createIndex(categories, 'normalizedName', 'normalizedName', { unique: true });
  createIndex(categories, 'kind', 'kind');

  const snapshots = prepareStore(database, transaction, 'snapshots');
  createIndex(snapshots, 'createdAt', 'createdAt');
};

const requestResult = <T>(request: IDBRequest<T>) => new Promise<T>((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error ?? new Error('The local database request failed.'));
});

const transactionComplete = (transaction: IDBTransaction) => new Promise<void>((resolve, reject) => {
  transaction.oncomplete = () => resolve();
  transaction.onabort = () => reject(transaction.error ?? new Error('The local database transaction was cancelled.'));
  transaction.onerror = () => reject(transaction.error ?? new Error('The local database transaction failed.'));
});

export const explainStorageError = (error: unknown) => {
  if (error instanceof DOMException && error.name === 'QuotaExceededError') {
    return 'Your browser storage is full. Existing planner data was kept. Free some space and try again.';
  }
  if (error instanceof DOMException && ['SecurityError', 'InvalidStateError', 'NotAllowedError'].includes(error.name)) {
    return 'Local storage is unavailable. Check private browsing or site storage permissions, then try again.';
  }
  return error instanceof Error ? error.message : 'The local database could not complete this action.';
};

export const openPlannerDatabase = () => {
  if (databasePromise) return databasePromise;
  databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      if (!request.transaction) return;
      try {
        configureSchema(request.result, request.transaction);
        if ((event as IDBVersionChangeEvent).oldVersion === 0) {
          const categories = request.transaction.objectStore('categories');
          DEFAULT_CATEGORIES.forEach((category) => categories.put(category));
        }
      } catch (error) {
        request.transaction.abort();
        reject(error);
      }
    };
    request.onblocked = () => window.dispatchEvent(new CustomEvent('spooky-plans-blocked'));
    request.onerror = () => {
      databasePromise = null;
      reject(request.error ?? new Error('The local planner database could not be opened.'));
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => {
        database.close();
        databasePromise = null;
        window.dispatchEvent(new CustomEvent('spooky-plans-versionchange'));
      };
      resolve(database);
    };
  });
  return databasePromise;
};

export const initializePlannerDatabase = async () => {
  return openPlannerDatabase();
};

const readAll = async <Name extends StoreName>(database: IDBDatabase, name: Name) => {
  const transaction = database.transaction(name, 'readonly');
  return requestResult(transaction.objectStore(name).getAll()) as Promise<StoreRecordMap[Name][]>;
};

export const loadPlannerData = async (): Promise<PlannerData> => {
  const database = await initializePlannerDatabase();
  const [tasks, taskOccurrences, events, notes, shoppingItems, categories] = await Promise.all([
    readAll(database, 'tasks'),
    readAll(database, 'taskOccurrences'),
    readAll(database, 'events'),
    readAll(database, 'notes'),
    readAll(database, 'shoppingItems'),
    readAll(database, 'categories')
  ]);
  return { tasks, taskOccurrences, events, notes, shoppingItems, categories };
};

const writePlannerData = (transaction: IDBTransaction, data: PlannerData) => {
  for (const name of PLANNER_STORE_NAMES) {
    const store = transaction.objectStore(name);
    store.clear();
    for (const item of data[name]) store.put(item);
  }
};

const replacePlannerDataTransaction = async (data: PlannerData) => {
  const database = await initializePlannerDatabase();
  const transaction = database.transaction([...PLANNER_STORE_NAMES], 'readwrite');
  writePlannerData(transaction, data);
  await transactionComplete(transaction);
};

export const replacePlannerData = async (data: PlannerData) => {
  await replacePlannerDataTransaction(data);
  announceChange();
};

export const replacePlannerStateWithRollback = async (
  next: BackupPayload,
  previousData: PlannerData,
  previousSettings: Settings
) => {
  await replacePlannerDataTransaction(next);
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(next.settings));
    announceChange();
  } catch (settingsError) {
    try {
      await replacePlannerDataTransaction(previousData);
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(previousSettings));
      announceChange();
    } catch (rollbackError) {
      throw new PlannerRollbackError(`Settings could not be saved and local rollback also failed: ${explainStorageError(rollbackError)}`);
    }
    throw settingsError;
  }
};

export const listSnapshots = async (): Promise<Snapshot[]> => {
  const database = await initializePlannerDatabase();
  const transaction = database.transaction('snapshots', 'readonly');
  const snapshots = await requestResult(transaction.objectStore('snapshots').index('createdAt').getAll()) as Snapshot[];
  await transactionComplete(transaction);
  return snapshots.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
};

export const createSnapshot = async (data: PlannerData, settings: Settings, appVersion: string): Promise<Snapshot> => {
  const database = await initializePlannerDatabase();
  const transaction = database.transaction('snapshots', 'readwrite');
  const store = transaction.objectStore('snapshots');
  const existing = await requestResult(store.index('createdAt').getAll()) as Snapshot[];
  const snapshot: Snapshot = {
    id: `snapshot-${crypto.randomUUID()}`,
    createdAt: new Date().toISOString(),
    appVersion,
    schemaVersion: 2,
    counts: getBackupCounts(data),
    payload: { ...data, settings }
  };
  store.put(snapshot);
  for (const snapshotId of getSnapshotIdsToDelete(existing)) store.delete(snapshotId);
  await transactionComplete(transaction);
  return snapshot;
};

export const deleteSnapshot = async (id: string) => {
  const database = await initializePlannerDatabase();
  const transaction = database.transaction('snapshots', 'readwrite');
  transaction.objectStore('snapshots').delete(id);
  await transactionComplete(transaction);
};

const announceChange = () => changeChannel?.postMessage({ type: 'data-changed', at: Date.now() });

export const saveRecord = async (name: ContentStore, value: PlannerRecord) => {
  const database = await initializePlannerDatabase();
  const transaction = database.transaction(name, 'readwrite');
  transaction.objectStore(name).put(value);
  await transactionComplete(transaction);
  announceChange();
};

export const deleteRecord = async (name: ContentStore, id: string) => {
  const database = await initializePlannerDatabase();
  const transaction = database.transaction(name === 'tasks' ? ['tasks', 'taskOccurrences'] : [name], 'readwrite');
  transaction.objectStore(name).delete(id);
  if (name === 'tasks') {
    const cursorRequest = transaction.objectStore('taskOccurrences').index('taskId').openCursor(IDBKeyRange.only(id));
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) return;
      cursor.delete();
      cursor.continue();
    };
  }
  await transactionComplete(transaction);
  announceChange();
};

export const saveTaskOccurrence = async (value: TaskOccurrence) => {
  const database = await initializePlannerDatabase();
  const transaction = database.transaction('taskOccurrences', 'readwrite');
  transaction.objectStore('taskOccurrences').put(value);
  await transactionComplete(transaction);
  announceChange();
};

export const saveCategory = async (value: Category) => {
  const database = await initializePlannerDatabase();
  const transaction = database.transaction('categories', 'readwrite');
  transaction.objectStore('categories').put(value);
  await transactionComplete(transaction);
  announceChange();
};

export const deleteCategoryAndDetach = async (categoryId: string) => {
  const database = await initializePlannerDatabase();
  const storeNames: ContentStore[] = ['tasks', 'events', 'notes', 'shoppingItems'];
  const transaction = database.transaction([...storeNames, 'categories'], 'readwrite');
  for (const name of storeNames) {
    const store = transaction.objectStore(name);
    const cursorRequest = store.openCursor();
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) return;
      const value = cursor.value as PlannerRecord;
      if (value.categoryId === categoryId) cursor.update({ ...value, categoryId: null, updatedAt: new Date().toISOString() });
      cursor.continue();
    };
  }
  transaction.objectStore('categories').delete(categoryId);
  await transactionComplete(transaction);
  announceChange();
};

export const subscribeToDatabaseChanges = (listener: () => void) => {
  if (!changeChannel) return () => undefined;
  const handleMessage = () => listener();
  changeChannel.addEventListener('message', handleMessage);
  return () => changeChannel.removeEventListener('message', handleMessage);
};
